import { useEffect, useState } from "react";
import { addIncome, updateIncome } from "./TransactionService";
import { getAllAccounts } from "../accounts/AccountService";
import { getAllCategories } from "../categories/CategoryService";
import { getAllTags } from "../tags/TagService";
import "./IncomeForm.css";
import {
  buildIncomeFormInitialValues,
  formatAccountLabel,
  readErrorMessage,
} from "./transactionUtils";

export default function IncomeForm({
  initialValues,
  submitLabel = "Save Income",
  title = "Add Income",
  onSaved,
  onCancel,
  replaceTransactionId,
}) {

  const [accounts,setAccounts]=useState([]);
  const [categories,setCategories]=useState([]);
  const [tags,setTags]=useState([]);
  const [errors, setErrors] = useState({});

  const [form,setForm]=useState(buildIncomeFormInitialValues(initialValues));

  useEffect(()=>{ load(); },[]);

  useEffect(() => {
    setForm(buildIncomeFormInitialValues(initialValues));
    setErrors({});
  }, [initialValues]);

  const load = async ()=>{
    setAccounts(await getAllAccounts());
    setCategories(await getAllCategories());
    setTags(await getAllTags());
  };

  const validate = (nextForm)=>{
    const nextErrors = {};
    if (!nextForm.accountId) nextErrors.accountId = "Select an account.";
    if (!nextForm.categoryId) nextErrors.categoryId = "Select a category.";
    const amount = parseFloat(nextForm.amount);
    if (!nextForm.amount || Number.isNaN(amount) || amount <= 0) nextErrors.amount = "Enter a valid amount.";
    if (!nextForm.date) nextErrors.date = "Select a date.";
    return nextErrors;
  };

  const updateField = (field, value)=>{
    setForm(f=>({ ...f, [field]: value }));
    if (errors[field]) {
      setErrors(e=>({ ...e, [field]: undefined }));
    }
  };

  const toggleTag = (id)=>{
    setForm(f=>({
      ...f,
      tags: f.tags.includes(id) ? f.tags.filter(t=>t!==id) : [...f.tags,id]
    }));
  };

  const submit = async (e)=>{
    e.preventDefault();
    const nextErrors = validate(form);
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }
    try {
      const res = replaceTransactionId ? await updateIncome(replaceTransactionId, form) : await addIncome(form);
      if (!res?.ok) {
        const message = await readErrorMessage(res, "Income not saved.");
        alert(message);
        return;
      }
      alert(replaceTransactionId ? "Income updated" : "Income added");
      if (!replaceTransactionId) {
        setForm(buildIncomeFormInitialValues());
      }
      onSaved?.();
    } catch (err) {
      alert(err?.message || "Income not saved.");
    }
  };

  return (
    <form onSubmit={submit}>
      <div className="card">
        <div className="card-title">{title}</div>
        <div className="form-grid">
      
          <div className="transaction-field">
            <select
              className={`input ${errors.accountId ? "transaction-input-error" : ""}`}
              value={form.accountId}
              onChange={e=>updateField("accountId", e.target.value)}
              aria-invalid={Boolean(errors.accountId)}
              required
            >
              <option value="">Select Account</option>
              {accounts.map(a=><option key={a.id} value={a.id}>{formatAccountLabel(a)}</option>)}
            </select>
            {errors.accountId && <div className="transaction-error-text">{errors.accountId}</div>}
          </div>

        <div className="transaction-field">
          <select
            className={`input ${errors.categoryId ? "transaction-input-error" : ""}`}
            value={form.categoryId}
            onChange={e=>updateField("categoryId", e.target.value)}
            aria-invalid={Boolean(errors.categoryId)}
            required
          >
            <option value="">Select Category</option>
            {categories
              .filter((c) => String(c.categoryType || "").toLowerCase() !== "expense")
              .map(c=><option key={c.id} value={c.id}>{c.categoryName}</option>)}
          </select>
          {errors.categoryId && <div className="transaction-error-text">{errors.categoryId}</div>}
        </div>

      <div className="transaction-field">
        <input
          className={`input ${errors.amount ? "transaction-input-error" : ""}`}
          type="number"
          step="0.01"
          min="0.01"
          inputMode="decimal"
          placeholder="Amount"
          value={form.amount}
          onChange={e=>updateField("amount", e.target.value)}
          aria-invalid={Boolean(errors.amount)}
          required
        />
        {errors.amount && <div className="transaction-error-text">{errors.amount}</div>}
      </div>

      <div className="transaction-field">
        <input
          className={`input ${errors.date ? "transaction-input-error" : ""}`}
          type="date"
          value={form.date}
          onChange={e=>updateField("date", e.target.value)}
          aria-invalid={Boolean(errors.date)}
          required
        />
        {errors.date && <div className="transaction-error-text">{errors.date}</div>}
      </div>
        </div>

        <div className="transaction-field form-full">
          <textarea className="input" placeholder="Description"
          value={form.description}
          onChange={e=>updateField("description", e.target.value)} />
        </div>


      {/* Tags */}
      <div className="income-form-tags-wrap">
        {tags.map(t=>(
          <label key={t.id} className="income-form-tag-label">
            <input type="checkbox" checked={form.tags.includes(t.id)} onChange={()=>toggleTag(t.id)} />
            {t.tagName}
          </label>
        ))}
      </div>
        <div className="income-form-save-wrap">
            {onCancel && (
              <button className="btn" type="button" onClick={onCancel}>Cancel</button>
            )}
            <button className="btn btn-primary" type="submit">{submitLabel}</button>
        </div>
      </div>

      
      
    </form>
  );
}
